
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { SupplierModel } from '@/models/supplier';
import { SupplierList } from '@/collections/suppliers';
import FeatureLockedNotify from '@/components/FeatureLockedNotify.vue';
import ShopifyMigrationWizard from '@/views/supplier/ShopifyMigrationWizard.vue';
import CsvImportWizard from '@/views/supplier/CsvImportWizard.vue';
import StockyMigrationWizard from '@/views/migration/StockyMigrationWizard.vue';
import SupplierEditDialog from '@/components/SupplierEditDialog.vue';
import { GlobalDefaults } from '@/models/supplier';
import { StoreModel } from '@/models/store';
import { EventBus } from '@/main';
import { ReplenishmentSettingHelper } from '@/helpers/ReplenishmentSettingHelper';
import { ReorderSettingParam } from '@/models/reorder_setting_param';

type MigrationSource = 'vendor' | 'metafield';

interface DataTableOptions {
  page: number;
  itemsPerPage: number;
  sortBy?: string[];
  sortDesc?: boolean[];
}

@Component({
  components: {
    FeatureLockedNotify,
    ShopifyMigrationWizard,
    CsvImportWizard,
    StockyMigrationWizard,
    SupplierEditDialog,
  }
})
export default class SupplierListView extends Vue {
  private suppliers = new SupplierList();
  private loading = false;
  private filters = {
    status: '',
    searchQuery: ''
  };

  // Dialog states
  private isMigrationWizardVisible = false;
  private isCsvWizardVisible = false;
  private isStockyWizardVisible = false;
  private migrationSource: MigrationSource | null = null;
  private confirmDeleteDialogVisible: boolean = false;
  private supplierToDelete: SupplierModel | null = null;
  private deleteLoading: boolean = false;
  private editDialogVisible: boolean = false;
  private selectedSupplier: SupplierModel | null = null;
  private editDialogLoading: boolean = false;
  private globalDefaults: GlobalDefaults = {};

  // Filter panel
  private filterPanelExpanded: number | undefined = 0;

  // Debounced search handler
  private debouncedSearch: any = null;

  // Data table options
  private totalSuppliers = 0;
  private options: DataTableOptions = {
    page: 1,
    itemsPerPage: 10
  };

  private metrics = [
    {
      label: 'Total Suppliers',
      value: 0,
      icon: 'mdi-domain',
      color: '#59C8E4'
    },
    {
      label: 'Active Suppliers',
      value: 0,
      icon: 'mdi-check-circle',
      color: '#4CAF50'
    },
    {
      label: 'Average Lead Time',
      value: '0 days',
      icon: 'mdi-clock-outline',
      color: '#FF9800'
    }
  ];

  private headers = [
    { text: 'Name', value: 'name', width: '200px' },
    { text: 'Status', value: 'status', width: '140px' },
    { text: 'Contact', value: 'contactEmail', width: '200px' },
    { text: 'Currency', value: 'currency_code', width: '100px' },
    { text: 'Products', value: 'productCount', width: '120px' },
    { text: 'Lead Time', value: 'leadTime', width: '120px' },
    { text: 'Actions', value: 'actions', sortable: false, align: 'center', width: '180px' }
  ];

  private statusOptions = [
    { text: 'All Status', value: '' },
    { text: 'Active', value: 'active' },
    { text: 'Inactive', value: 'inactive' }
  ];

  @Prop({ default: () => new StoreModel() })
  private store!: StoreModel;

  @Watch('options', { deep: true })
  async onOptionsUpdate() {
    await this.fetchSuppliers();
  }

  async created() {
    // Create debounced search function (300ms delay)
    this.debouncedSearch = this.debounce(this.applyFilterAndResetPage, 300);
  }

  async mounted() {
    EventBus.$on('supplier-migration-complete', this.fetchSuppliers);
    await this.fetchGlobalDefaults();
  }

  private async fetchGlobalDefaults() {
    try {
      const params = await ReplenishmentSettingHelper.getGlobalParams(
        {
          product: 'variant',
          location: '',
          supplier: 'global',
          parameter: 'global',
        },
        0
      );
      this.globalDefaults = {
        leadtime: params.leadtime,
        orderCycle: params.orderCycle,
        minStockCoverDays: params.minStockCoverDays,
      };
    } catch (error) {
      console.error('Failed to fetch global defaults:', error);
    }
  }

  beforeDestroy() {
    EventBus.$off('supplier-migration-complete', this.fetchSuppliers);
    // Clean up debounce timer
    if (this.debouncedSearch && this.debouncedSearch.cancel) {
      this.debouncedSearch.cancel();
    }
  }

  private async fetchSuppliers() {
    this.loading = true;
    const { page, itemsPerPage } = this.options;
    const previousItems = this.suppliers.items;
    try {
      this.suppliers.items = [];
      const fetchOptions: { page: number; limit: number; search?: string } = { page, limit: itemsPerPage };
      const trimmedSearch = this.filters.searchQuery?.trim();
      if (trimmedSearch) {
        fetchOptions.search = trimmedSearch;
      }
      const data = await this.suppliers.fetch(fetchOptions);
      this.totalSuppliers = data?.pagination?.total || 0;
      this.updateMetrics(data?.metrics);
    } catch (error) {
      this.suppliers.items = previousItems;
      console.error("Failed to fetch suppliers:", error);
    } finally {
      this.loading = false;
    }
  }

  private updateMetrics(metrics: any) {
    if (!metrics) return;
    this.metrics[0].value = metrics.totalSuppliers;
    this.metrics[1].value = metrics.activeSuppliers;
    this.metrics[2].value = `${metrics.avgLeadTime} days`;
  }

  get filteredSuppliers(): SupplierModel[] {
    let suppliers = this.suppliers.items;

    // Search is handled server-side via the API search parameter
    // Status filter is applied client-side as the backend doesn't support it
    if (this.filters.status) {
      const statusFilter = this.filters.status.toLowerCase();
      suppliers = suppliers.filter(s =>
        s.status && s.status.toLowerCase() === statusFilter
      );
    }

    return suppliers;
  }

  get hasActiveFilters(): boolean {
    return !!(
      this.filters.searchQuery ||
      this.filters.status
    );
  }

  get activeFilterCount(): number {
    let count = 0;
    if (this.filters.searchQuery) count++;
    if (this.filters.status) count++;
    return count;
  }

  private applyFilterAndResetPage() {
    // Normalize search query so whitespace-only input doesn't show as active filter
    this.filters.searchQuery = this.filters.searchQuery?.trim() || '';
    if (this.options.page !== 1) {
      // Setting page to 1 triggers the options watcher, which calls fetchSuppliers
      this.options.page = 1;
    } else {
      // Page is already 1, watcher won't fire, so fetch directly
      this.fetchSuppliers();
    }
  }

  private clearAllFilters() {
    this.filters.searchQuery = '';
    this.filters.status = '';
    this.applyFilterAndResetPage();
  }

  private getStatusLabel(status: string): string {
    const option = this.statusOptions.find(opt => opt.value === status);
    return option ? option.text : status;
  }

  private getStatusColor(status: string): string {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return '#4CAF50';
      case 'INACTIVE':
        return '#9E9E9E';
      default:
        return '#9E9E9E';
    }
  }

  private getStatusIcon(status: string): string {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'mdi-check-circle';
      case 'INACTIVE':
        return 'mdi-pause-circle';
      default:
        return 'mdi-information';
    }
  }

  private formatStatus(status: string): string {
    if (!status) return 'Unknown';
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  }

  private getMetricBgColor(color: string): string {
    // Create a lighter version of the color for background
    const colorMap: Record<string, string> = {
      '#59C8E4': 'rgba(89, 200, 228, 0.1)',
      '#4CAF50': 'rgba(76, 175, 80, 0.1)',
      '#FF9800': 'rgba(255, 152, 0, 0.1)'
    };
    return colorMap[color] || 'rgba(0, 0, 0, 0.05)';
  }

  private viewSupplier(supplier: SupplierModel) {
    this.$router.push({
      name: 'supplier',
      params: { id: supplier.id.toString() }
    });
  }

  private createPO(supplier: SupplierModel) {
    this.$router.push({
      name: 'createPurchaseOrder',
      params: { supplierId: supplier.id.toString() }
    });
  }

  private createSupplier() {
    this.$router.push({ name: 'supplierCreate' });
  }

  private navigateToImportSuppliers() {
    this.isCsvWizardVisible = true;
  }

  private openMigrationWizard(source: MigrationSource) {
    this.migrationSource = source;
    this.isMigrationWizardVisible = true;
  }

  private closeMigrationWizard() {
    this.isMigrationWizardVisible = false;
    this.migrationSource = null;
  }

  private openStockyWizard() {
    this.isStockyWizardVisible = true;
  }

  private closeStockyWizard() {
    this.isStockyWizardVisible = false;
    this.fetchSuppliers();
  }

  private get canManageSuppliers(): boolean {
    const validateValues = [
      {
        code: 'supplier_management',
        value: 'unlocked'
      }
    ];
    const validateResult = this.store.validateRule(validateValues);
    return validateResult.isValidate;
  }

  private showDeleteConfirmation(supplier: SupplierModel) {
    this.supplierToDelete = supplier;
    this.confirmDeleteDialogVisible = true;
  }

  private async confirmDeleteSupplier() {
    if (!this.supplierToDelete) return;

    this.deleteLoading = true;
    try {
      const supplier = new SupplierModel(this.supplierToDelete);
      await supplier.remove();

      EventBus.$emit('show-snackbar', { message: `Supplier '${this.supplierToDelete.name}' deleted successfully.`, color: 'success' });
      await this.fetchSuppliers();

    } catch (error: any) {
      console.error('Error deleting supplier:', error);
      EventBus.$emit('show-snackbar', { message: `Failed to delete supplier: ${error.message || 'Unknown error'}`, color: 'error' });
    } finally {
      this.confirmDeleteDialogVisible = false;
      this.supplierToDelete = null;
      this.deleteLoading = false;
    }
  }

  closeCsvWizard() {
    this.isCsvWizardVisible = false;
  }

  proceedToCsvImport() {
    this.isCsvWizardVisible = false;
    this.$router.push({ name: 'SupplierImportUpload' });
  }

  private editSupplierParameters(supplier: SupplierModel) {
    this.selectedSupplier = supplier;
    this.editDialogVisible = true;
  }

  private closeEditDialog() {
    this.editDialogVisible = false;
    this.selectedSupplier = null;
  }

  private async handleSaveSupplierParams(payload: { params: ReorderSettingParam; forceUpdate: boolean }) {
    if (!this.selectedSupplier) return;

    this.editDialogLoading = true;
    try {
      const supplier = new SupplierModel(this.selectedSupplier);

      // Normal Mode: Update all params to supplier record
      // Force Update is disabled on this page (showForceUpdate=false)
      supplier.leadTime = payload.params.leadtime ?? 0;
      supplier.reorderCycle = payload.params.orderCycle ?? 0;
      supplier.minStockCoverDays = payload.params.minStockCoverDays ?? 0;
      supplier.minOrderValue = payload.params.minOrderValue ?? 0;
      supplier.maxOrderValue = payload.params.maxOrderValue ?? 0;
      supplier.minOrderQuantity = payload.params.minOrderQuantity ?? 0;
      supplier.maxOrderQuantity = payload.params.maxOrderQuantity ?? 0;
      supplier.scope = payload.params.scope;

      // Update supplier (PUT /suppliers/${id})
      await supplier.update({ forceUpdate: false });

      EventBus.$emit('show-snackbar', {
        message: 'Supplier parameters updated successfully.',
        color: 'success'
      });

      // Refresh the supplier list
      await this.fetchSuppliers();

      // Close the dialog
      this.closeEditDialog();

    } catch (error: any) {
      console.error('Error updating supplier parameters:', error);
      EventBus.$emit('show-snackbar', {
        message: `Failed to update supplier parameters: ${error.message || 'Unknown error'}`,
        color: 'error'
      });
    } finally {
      this.editDialogLoading = false;
    }
  }

  /**
   * Debounce utility function
   */
  private debounce(func: Function, wait: number) {
    let timeout: number | null = null;

    const debounced = (...args: any[]) => {
      if (timeout !== null) {
        clearTimeout(timeout);
      }
      timeout = window.setTimeout(() => {
        func.apply(this, args);
      }, wait);
    };

    debounced.cancel = () => {
      if (timeout !== null) {
        clearTimeout(timeout);
        timeout = null;
      }
    };

    return debounced;
  }
}
